import React from "react";
import { updateOffice, insertOffice, setCampaignOfficesLoading, setCampaignOfficesError } from "../../../store/actions";
import { connect } from "react-redux";
import DataTable from "../../theme/tables/DataTable";
import BaseModal from "../../theme/modals/BaseModal";
import BaseForm from "../../theme/forms/BaseForm";
import Moment from "moment";
import api from "../../../store/api/api";
import { Button, Grid, LinearProgress } from "@mui/material";

const ERROR_TIMEOUT = 10000;

class OfficesTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editModalOpen: false,
      editModalErrorMsg: null,
      deleteModalOpen: false,
      editingInstance: {},
      showCampaignEditSection: true,
      formInputs: [],
      geocodeError: false,
      geoErrorMessage: "",
      isLoading: false,
      operationError: null,
      uberOrganizations: [],
      deactivatedOfficeView: false,
      offices: [],
      inactiveOffices: [],
      fetchLoading: false,
    };
  }

  componentDidMount() {
    this.fetchOffices();
    api.get("uber-organizations/").then((res) => {
      this.setState({ uberOrganizations: res.data });
    }).catch((err) => console.log(err));
  }

  fetchOffices = (showInactive = this.state.deactivatedOfficeView) => {
    this.setState({ fetchLoading: true });
    const params = showInactive ? { deactivated: 1 } : {};

    return api
      .get("offices/", params)
      .then((res) => {
        if (showInactive) {
          this.setState({ inactiveOffices: res.data });
        } else {
          this.setState({ offices: res.data });
        }
      })
      .catch((err) => {
        console.log(err);
        this.props.setError("Failed to fetch offices");
      })
      .finally(() => {
        this.setState({ fetchLoading: false });
      });
  };

  toggleOfficeView = () => {
    const showInactive = !this.state.deactivatedOfficeView;
    this.setState({ deactivatedOfficeView: showInactive }, () => {
      this.fetchOffices(showInactive);
    });
  };

  updateOrCreateOffice = (data) => {
    this.setState({ isLoading: true, operationError: null });

    if (Object.keys(data).includes("id")) {
      return api
        .patch(`offices/${data.id}/`, data)
        .then((res) => {
          this.props.updateOffice(res.data);
          this.setState({ isLoading: false });
          return res.data;
        })
        .catch((err) => {
          console.log(err);
          const errorMessage = err.response?.data?.error || "Failed to update office";
          this.setState({ 
            isLoading: false, 
            operationError: errorMessage 
          });
          setTimeout(() => {
            this.setState({ operationError: null });
          }, ERROR_TIMEOUT);
          throw err;
        });
    } else {
      return api.post("offices/", data).then((res) => {
        this.props.insertOffice(res.data);
        this.setState({ isLoading: false });
        return res.data;
      }).catch((err) => {
        console.log(err);
        const errorMessage = err.response?.data?.error || "Failed to create office";
        this.setState({ 
          isLoading: false, 
          operationError: errorMessage 
        });
        setTimeout(() => {
          this.setState({ operationError: null });
        }, ERROR_TIMEOUT);
        throw err;
      });
    }
  };

  _filterKeysForOfficeDataTable = () => {
    const offices = this.state.deactivatedOfficeView
      ? this.state.inactiveOffices
      : this.state.offices;

    return offices.map((d) => ({
      name: d.name,
      address: d.addressline1 + " " + d.addressline2,
      city: d.city,
      state: d.statecode,
      zip: d.zip5,
      uber_org: d.uber_organization?.name || "-",
      created_on: new Moment(d.created_at).format("MM/DD/YY"),
      _created_on: new Moment(d.created_at),
      _data: d,
    }));
  };

  _generateFormInputs = (creating = false, instance = {}) => {
    return [
      {
        type: "text",
        title: "Office Name",
        dataKey: "name",
        placeholder: "Pleasantville",
        value: instance.name || "",
      },
      {
        type: "text",
        title: "Address Line 1",
        dataKey: "addressline1",
        placeholder: "123 Main",
        value: instance.addressline1 || "",
      },
      {
        type: "text",
        title: "Address Line 2",
        dataKey: "addressline2",
        placeholder: "Suite 100",
        value: instance.addressline2 || "",
      },
      {
        type: "text",
        title: "City",
        dataKey: "city",
        placeholder: "Pleasantville",
        value: instance.city || "",
      },
      {
        type: "text",
        title: "State",
        dataKey: "statecode",
        placeholder: "Two Character State Code",
        value: instance.statecode || "",
      },
      {
        type: "text",
        title: "ZipCode",
        dataKey: "zip5",
        placeholder: creating
          ? "At least five characters, with at least one number and letter"
          : "Leave blank if you don't want to change this.",
        value: instance.zip5 || "",
      },
      {
        type: "select",
        title: "Uber Organization",
        dataKey: "uber_organization",
        value: instance.uber_organization?.id ?? instance.uber_organization ?? "",
        options: [
          { value: "", display: "None" },
          ...this.state.uberOrganizations.map((o) => ({ value: o.id, display: `${o.name} (${o.uber_org_id})` })),
        ],
      },
    ];
  };

  onOpenEditModal = (modelInstance = {}, creating = false) => {
    this.setState({
      formInputs: this._generateFormInputs(creating, modelInstance),
      editModalOpen: true,
      editingInstance: modelInstance,
    });
  };

  onCloseEditModal = () => {
    this.setState({
      editModalOpen: false,
      editingInstance: {},
      editModalErrorMsg: null,
    });
  };

  onOpenDeleteModal = (modelInstance = {}) => {
    this.setState({ deleteModalOpen: true, editingInstance: modelInstance });
  };

  onCloseDeleteModal = () => {
    this.setState({ deleteModalOpen: false, editingInstance: {} });
  };

  handleUpdateCreateSubmission = (data) => {
    const id = this.state.editingInstance.id;
    if (id) data = { ...data, id };

    if (data.uber_organization) {
      data = { ...data, uber_organization: parseInt(data.uber_organization, 10) };
    } else {
      data = { ...data, uber_organization: null };
    }

    data = { ...data, campaign: this.props.focusedCampaign.id };
    this.geocode(data)
      .then((res) => {
        data.lat = res[0].geometry.location.lat;
        data.lon = res[0].geometry.location.lng;
        this.updateOrCreateOffice(data).then(() => {
          this.onCloseEditModal();
          this.fetchOffices();
        }).catch((err) => {
          this.setState({ 
            editModalErrorMsg: this.state.operationError || "Failed to save office"
          });
        });
      })
      .catch((err) => {
        this.setState({ 
          editModalErrorMsg: err.message,
          geocodeError: true, 
          geoErrorMessage: err.message 
        });
        setTimeout(() => this.setState({ geocodeError: false }), 10000);
      });
  };

  handleRemoveSubmission = () => {
    const id = this.state.editingInstance.id;
    this.updateOrCreateOffice({ is_active: false, id })
      .then(() => {
        this.onCloseDeleteModal();
        this.fetchOffices();
      })
      .catch((err) => {
        console.error("Failed to remove office:", err);
      });
  };

  handleReactivateSubmission = () => {
    const id = this.state.editingInstance.id;
    this.updateOrCreateOffice({ is_active: true, id })
      .then(() => {
        this.onCloseDeleteModal();
        this.fetchOffices();
      })
      .catch((err) => {
        console.error("Failed to reactivate office:", err);
      });
  };

  geocode = (data) => {
    const address = [
      data.addressline1,
      data.addressline2,
      data.city,
      data.statecode,
      data.zip5,
    ]
      .filter((part) => part != null && part !== "")
      .join(" ");

    return api
      .get("/geocode", { address })
      .then((res) => {
        const { status, results = [] } = res.data;
        if (status === "OK" && results.length > 0) {
          return results;
        }
        return Promise.reject(new Error("No Matches Found"));
      })
      .catch((err) => {
        if (err.message === "No Matches Found") {
          return Promise.reject(err);
        }
        return Promise.reject(
          new Error(
            "Unable To Create Office. Please check address for errors and try again."
          )
        );
      });
  };

  render() {
    const { focusedCampaign, canViewInactive } = this.props;
    const { showCampaignEditSection, deactivatedOfficeView, editingInstance } = this.state;

    const tableActions = deactivatedOfficeView
      ? [
          {
            title: "edit",
            icon: "fa-edit",
            action: (modelInstance) => this.onOpenEditModal(modelInstance),
          },
          {
            title: "Reactivate",
            icon: "fa-plus-square",
            action: this.onOpenDeleteModal,
          },
        ]
      : [
          {
            title: "edit",
            icon: "fa-edit",
            action: (modelInstance) => this.onOpenEditModal(modelInstance),
          },
          {
            title: "Remove",
            icon: "fa-minus-square",
            action: this.onOpenDeleteModal,
          },
        ];

    return (
      <div>
        {showCampaignEditSection && (
          <div className="top-section-large">
            <div className="row">
              <div className="column column-40">
                <h2>{focusedCampaign.name}</h2>
                <p>{focusedCampaign.description}</p>
              </div>
              <div className="column column-20">
                <div className="row">
                  <div className="column">
                    <div
                      className="button secondary-milligram"
                      onClick={this.props.handleEditFocusedCampaign}
                    >
                      Edit
                    </div>
                  </div>
                  <div className="column">
                    <div
                      className="button danger"
                      onClick={this.props.handleRemoveFocusedCampaign}
                    >
                      Deactivate Campaign
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="button-table-top-wrapper">
          <Grid container spacing={2} sx={{ marginBottom: "10px" }}>
            <Grid item>
              <a
                className={`button ${this.state.isLoading ? "disabled" : ""}`}
                onClick={() => !this.state.isLoading && this.onOpenEditModal({}, true)}
              >
                {this.state.isLoading ? "Processing..." : "+ Add Office"}
              </a>
            </Grid>
            {canViewInactive && (
              <Grid item>
                {!deactivatedOfficeView ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    disableElevation={true}
                    startIcon={<i className="fas fa-minus-square" />}
                    onClick={this.toggleOfficeView}
                  >
                    Show Inactive Offices
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    disableElevation={true}
                    startIcon={<i className="fas fa-plus-square" />}
                    onClick={this.toggleOfficeView}
                  >
                    Show Active Offices
                  </Button>
                )}
              </Grid>
            )}
          </Grid>
        </div>

        {(this.state.fetchLoading || this.state.isLoading) && (
          <LinearProgress sx={{ marginBottom: "10px" }} />
        )}

        {this.state.operationError && (
          <div className="danger-text flx center" style={{ margin: '10px 0' }}>
            {this.state.operationError}
          </div>
        )}

        {this.state.geocodeError && (
          <div className="danger-text flx center" style={{ margin: '10px 0' }}>
            {this.state.geoErrorMessage}
          </div>
        )}

        <DataTable
          data={this._filterKeysForOfficeDataTable()}
          title="Offices"
          actions={tableActions}
        />

        <BaseModal
          open={this.state.editModalOpen}
          onClose={this.onCloseEditModal}
          center
        >
          {this.state.isLoading && <LinearProgress />}
          <BaseForm
            inputs={this.state.formInputs}
            onCancel={this.onCloseEditModal}
            onSubmit={this.handleUpdateCreateSubmission}
            isLoading={this.state.isLoading}
          />
          {!!this.state.editModalErrorMsg && (
            <div className="danger-text flx center">
              {this.state.editModalErrorMsg}
            </div>
          )}
        </BaseModal>

        <BaseModal
          open={this.state.deleteModalOpen}
          onClose={this.onCloseDeleteModal}
          center
        >
          <div className="modal-internal-wrapper">
            <p>
              Are you sure you want to {deactivatedOfficeView ? "reactivate" : "remove"} the office{" "}
              {this.state.editingInstance.name}?
            </p>
            {this.state.operationError && (
              <div className="danger-text flx center" style={{ margin: '10px 0' }}>
                {this.state.operationError}
              </div>
            )}
            <div className="button-wrapper">
              <div 
                className={`button ${this.state.isLoading ? 'disabled' : ''}`} 
                onClick={!this.state.isLoading ? (deactivatedOfficeView ? this.handleReactivateSubmission : this.handleRemoveSubmission) : undefined}
              >
                {this.state.isLoading ? (deactivatedOfficeView ? 'Reactivating...' : 'Removing...') : (deactivatedOfficeView ? 'Reactivate' : 'Remove')}
              </div>
              <div
                className="button button-outline"
                onClick={this.onCloseDeleteModal}
              >
                Cancel
              </div>
            </div>
          </div>
        </BaseModal>
      </div>
    );
  }
}

export default connect(null, { updateOffice, insertOffice, setCampaignOfficesLoading, setCampaignOfficesError })(OfficesTab); 